import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import Button from "../../Button/Button";
import React, { useContext, useEffect, useRef, useState } from "react";
import { ToastContext } from "../../../contexts/ToastContext/ToastContext";
import { Dialog } from "@mui/material";
import SubscriptionDialog from "../../Dialogs/SubscriptionDialog";
import { ToastVariants } from "../../../models";

const Step3 = ({ value, onChange, changeStep }: { value: string; onChange: (val: string) => void; changeStep: (val?: boolean) => void }) => {
  const toast = useContext(ToastContext);
  const editorRef = useRef<ReactQuill>(null);
  const MAX_CHAR_LIMIT = 200;
  const [showPreview, setShowPreview] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const modules = {
    toolbar: [
      ["bold", "italic", "underline"],
      [{ list: "ordered" }, { list: "bullet" }],
    ],
  };

  useEffect(() => {
    const quill = editorRef.current?.editor as any;
    if (!quill) return;

    const handleTextChange = (delta: any, oldDelta: any, source: string) => {
      if (source !== "user") return;

      const text = quill.getText().trim();
      const nextLength = text.length;
      const insertedText = delta.ops?.find((op: any) => typeof op.insert === "string")?.insert || "";

      if (insertedText && nextLength > MAX_CHAR_LIMIT) {
        quill.history.undo();
        toast?.openToast("You can’t type more than 200 characters.", 2000, ToastVariants.Error);
        return;
      }

      setCharCount(Math.min(nextLength, MAX_CHAR_LIMIT));
    };
    quill.on("text-change", handleTextChange);
    return () => {
      quill.off("text-change", handleTextChange);
    };
  }, [toast]);

  useEffect(() => {
    const quill = editorRef.current?.editor as any;
    if (!quill) return;

    const text = quill.getText().trim();
    setCharCount(Math.min(text.length, MAX_CHAR_LIMIT));
  }, [value]);

  const handleChange = (val: string) => {
    const plainText = editorRef.current?.editor?.getText().trim() || "";
    if (plainText.length <= MAX_CHAR_LIMIT) {
      onChange(val);
    }
  };

  const handleNext = () => {
    const plainText = editorRef.current?.editor?.getText()?.trim();
    if (!plainText || plainText.length === 0) {
      toast?.openToast("Please complete patient notification.", 2000, ToastVariants.Error);
      return;
    }
    changeStep();
  };

  const disabled = charCount === 0 || value.length === 0;

  return (
    <>
      <div className="mt-10">
        <h2 className="headline font-bold uppercase">Annual Plan Description</h2>
        <div>
          <p className="my-5">Please enter your annual plan description below. This information will be visible to your patients.</p>
          <ReactQuill
            ref={editorRef}
            modules={modules}
            value={value}
            onChange={handleChange}
            placeholder={`Type up to ${MAX_CHAR_LIMIT} characters...`}
          />
          <p className="text-sm text-right mt-2">
            {charCount}/{MAX_CHAR_LIMIT}
          </p>
          {charCount >= MAX_CHAR_LIMIT}
          <p className="text-sm font-bold text-docnote-orange cursor-pointer mt-1 inline-block float-right" onClick={() => setShowPreview(true)}>
            Preview
          </p>
        </div>
      </div>
      <div className="fixed bottom-0 right-0 w-11/12 max-w-120 z-30 bg-docsigna-pink-light border-t">
        <div className="px-8 py-5 flex justify-end">
          <Button onClick={() => changeStep(true)} className="pointer px-5 mr-3" text={"Previous"} width="fit" variant="Secondary" />
          <div id="save-button">
            <Button disabled={disabled} onClick={handleNext} className="pointer px-5" text={"Next"} width="fit" variant={"Primary"} />
          </div>
        </div>
      </div>
      <Dialog open={showPreview} onClose={() => setShowPreview(false)} className="patient-block-fee-dialog">
        <SubscriptionDialog
          handleDialog={() => {
            toast?.openToast("Unable to complete this action in preview mode.", 2000, ToastVariants.Error);
          }}
          description={value ? value : "Information that you provide will be displayed here."}
          setShowPreview={setShowPreview}
        />
      </Dialog>
    </>
  );
};

export default Step3;
